import React, { createContext, useContext, useState, useCallback } from 'react';
import LoadingOverlay from '../components/ui/loading-overlay';

interface LoadingContextType {
  isLoading: boolean;
  setLoading: (loading: boolean, message?: string) => void;
  withLoading: <T>(
    operation: () => Promise<T>,
    message?: string
  ) => Promise<T>;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export function LoadingProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');

  const setLoading = useCallback((loading: boolean, message?: string) => {
    setIsLoading(loading);
    setLoadingMessage(message || '');
  }, []);

  const withLoading = useCallback(async <T,>(
    operation: () => Promise<T>,
    message?: string
  ): Promise<T> => {
    setLoading(true, message);
    try {
      const result = await operation();
      return result;
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        setLoading,
        withLoading,
      }}
    >
      {children}
      
      {/* Overlay de chargement global */}
      {isLoading && (
        <LoadingOverlay message={loadingMessage} />
      )}
    </LoadingContext.Provider>
  );
}

export function useLoading() {
  const context = useContext(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
}